/**
 * Discount Kit - Volume Picker custom element
 *
 * Custom element definition for `<dkl-volume-picker-radio-groups>`.
 * Enhances the rendered volume discount radios to:
 * - Sync the selected tier quantity into the nearest product form quantity selector
 * - Emit Theme `DiscountUpdateEvent` and Discount Kit `VolumeDiscountTierChangeEvent`
 * - Emit Discount Kit widget lifecycle events (mount / unmount)
 *
 * This file relies on the theme's import map for @theme/events, @dkl/events, and @dkl/global.
 */

import { DiscountUpdateEvent, ThemeEvents } from '@theme/events';
import { VolumeDiscountTierChangeEvent } from '@dkl/events';
import { DklWidgetBase } from '@dkl/global';

class DklVolumePickerRadioGroups extends DklWidgetBase {
  /** @type {import('@dkl/events').VolumeDiscountTier | null | undefined} */
  #lastTier = null;

  /** @type {boolean} */
  #currentVariantOnSale = false;

  /**
   * Whether this widget instance should sync with the quantity selector.
   * Controlled via `data-dkl-sync-with-quantity` on the custom element.
   */
  #shouldSyncWithQuantitySelector() {
    const raw = this.dataset.dklSyncWithQuantity;
    return raw === 'true' || raw === '1';
  }

  /**
   * Whether this widget instance should update the primary product price
   * when a tier is selected.
   * Controlled via `data-dkl-update-price-on-change` on the custom element.
   */
  #shouldUpdatePriceOnChange() {
    const raw = this.dataset.dklUpdatePriceOnChange;
    return raw === 'true' || raw === '1';
  }

  connectedCallback() {
    this.addEventListener('change', this.#onChange);
    document.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantitySelectorUpdate);
    this.#captureInitialTier();
    const widgetType = this.dataset.dklWidgetType ?? 'volume-picker';
    this.dispatchWidgetMount(widgetType);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantitySelectorUpdate);
    const widgetType = this.dataset.dklWidgetType ?? 'volume-picker';
    this.dispatchWidgetUnmount(widgetType);
  }

  /**
   * Handles radio change events inside the component.
   *
   * @param {Event} event
   */
  #onChange = (event) => {
    const target = event.target;

    if (!(target instanceof HTMLInputElement)) return;
    if (target.type !== 'radio') return;
    if (!target.classList.contains('dkl-volume-picker__radio')) return;
    if (!this.contains(target)) return;

    const selectedQuantity = Number.parseInt(target.value, 10);
    if (!Number.isFinite(selectedQuantity) || selectedQuantity <= 0) return;

    /** @type {any} */
    const e = event;
    const isFromQuantitySelectorSync = Boolean(e?.__fromQuantitySelectorSync);

    /**
     * Get the nearest product form for this widget.
     */
    let productForm = target.closest('product-form-component, form');

    if (!productForm) {
      const productId = this.dataset.dklProductId || null;
      const scopeRoot =
        this.closest('.shopify-section, .product-details') || this.parentElement || document;

      if (productId) {
        productForm =
          scopeRoot.querySelector(`product-form-component[data-product-id="${productId}"]`) ||
          document.querySelector(`product-form-component[data-product-id="${productId}"]`);
      } else {
        productForm =
          scopeRoot.querySelector('product-form-component') || document.querySelector('product-form-component');
      }
    }

    if (productForm && !isFromQuantitySelectorSync && this.#shouldSyncWithQuantitySelector()) {
      /** @type {any} */
      const quantitySelector =
        productForm.querySelector('quantity-selector-component') ||
        productForm.querySelector('quantity-selector');

      if (quantitySelector?.setValue) {
        quantitySelector.setValue(selectedQuantity);
        if (selectedQuantity > 1) {
          quantitySelector.querySelector('button[name="minus"]').removeAttribute('disabled');
        } else {
          quantitySelector.querySelector('button[name="minus"]').setAttribute('disabled', true);
        }
      } else {
        const quantityInput =
          /** @type {HTMLInputElement | null} */ (productForm.querySelector('input[name="quantity"]'));
        if (quantityInput) {
          quantityInput.value = String(selectedQuantity);
        }
      }
    }

    const rawProductId =
      (productForm instanceof HTMLElement && productForm.dataset.dklProductId) || this.dataset.dklProductId || null;
    const rawVariantId =
      (productForm instanceof HTMLElement && productForm.dataset.dklVariantId) || this.dataset.dklVariantId || null;

    const productId = rawProductId != null && rawProductId !== '' ? Number(rawProductId) : null;
    const variantId = rawVariantId != null && rawVariantId !== '' ? Number(rawVariantId) : null;

    const rawDiscountAmount = Number.parseFloat(target.dataset.dklDiscountAmount || '0');
    const discountType = target.dataset.dklDiscountType || '';
    const normalizedDiscountAmount =
      discountType === 'fixedAmount' ? rawDiscountAmount / 100 : rawDiscountAmount;

    const tier = {
      index: Number.parseInt(target.dataset.dklTierIndex || '0', 10),
      quantity: selectedQuantity,
      discountAmount: normalizedDiscountAmount,
      discountType,
      unitPriceCents: Number.parseInt(target.dataset.dklUnitPriceCents || '0', 10),
    };

    const previousTier = this.#lastTier || null;
    const currentTier = tier;
    this.#lastTier = currentTier;

    if (this.#shouldUpdatePriceOnChange()) {
      this.#updateProductPriceFromTier(currentTier);
    }

    const resource = {
      productId,
      variantId,
      widgetId: this.widgetId,
      widgetType: this.dataset.dklWidgetType || 'volume-picker',
      previousTier,
      currentTier,
    };

    this.log('volume-discount:tier-change', resource);

    // Discount Kit specific event
    this.dispatchEvent(new VolumeDiscountTierChangeEvent(resource));

    // Theme-level discount update event for broader integrations
    try {
      document.dispatchEvent(new DiscountUpdateEvent(resource, resource.widgetId || ''));
    } catch (error) {
      console.error('Failed to dispatch DiscountUpdateEvent for dkl volume picker', error);
    }
  };

  /**
   * Handles variant price updates from the theme and recomputes
   * per-tier prices for the currently selected product.
   *
   * @param {Event} event
   */
  #onVariantUpdate = (event) => {
    /** @type {any} */
    const e = event;
    const detail = e?.detail;
    const data = detail?.data;

    if (!data) return;

    const eventProductId = data.productId || data.newProduct?.id || null;
    const widgetProductId = this.dataset.dklProductId || null;

    if (!eventProductId || !widgetProductId || eventProductId.toString() !== widgetProductId.toString()) return;

    const variant = detail.resource;
    const variantPriceCents = variant?.price;

    if (!Number.isFinite(variantPriceCents)) return;

    this.#recomputeTierPrices(Number(variantPriceCents));

    // Detect sale state for this variant when possible (compare_at_price > price)
    const compareAtPriceCents = variant?.compare_at_price;
    const isOnSale =
      Number.isFinite(compareAtPriceCents) &&
      Number(compareAtPriceCents) > Number(variantPriceCents);

    this.#currentVariantOnSale = Boolean(isOnSale);

    // If this widget is configured to exclude sale prices, disable the
    // radio groups when the variant is on sale and update the per-tier
    // price labels to indicate unavailability, while letting the theme's
    // <product-price> component control the main display price.
    if (this.excludeSalePrices && this.#currentVariantOnSale) {
      this.#disableForSaleVariant();
      this.#setPriceUnavailableForSaleVariant();
      return;
    }

    // For non-sale variants (or when exclusion is off), ensure radios are enabled.
    this.#enableForNonSaleVariant();

    // Keep the main product price base value in sync with the currently
    // selected variant before applying any tier-based overrides.
    if (this.#shouldUpdatePriceOnChange()) {
      const productId = this.dataset.dklProductId;
      if (!productId) return;

      const scopeRoot =
        this.closest('.shopify-section, .product-details') || document;

      const productPrice = /** @type {HTMLElement | null} */ (
        scopeRoot.querySelector(`product-price[data-product-id="${productId}"]`)
      );
      if (!productPrice) return;

      const priceContainer = productPrice.querySelector('[ref="priceContainer"]');
      const priceSpan = priceContainer?.querySelector('.price');

      if (!(priceSpan instanceof HTMLElement)) return;

      // Always refresh the cached base price from the theme-rendered price
      // for this variant, so subsequent tier updates have the correct base.
      productPrice.dataset.dklBasePrice = priceSpan.textContent?.trim() || '';
    }

    // Optionally re-select the closest tier based on the current quantity
    // when coming from an ineligible variant back to an eligible one.
    if (this.#shouldSyncWithQuantitySelector()) {
      const currentQty = this.#getCurrentQuantityFromForm();
      if (currentQty && currentQty > 0) {
        this.#applyClosestTierForQuantity(currentQty);
      } else if (this.#shouldUpdatePriceOnChange()) {
        // No valid quantity to map to a tier, ensure we fall back to base price.
        this.#resetProductPrice();
      }
    }

    // After quantity/tier re-selection, ensure the product price reflects the
    // currently active tier when price syncing is on. For sale variants, this
    // only updates the primary price; existing theme compare-at markup is left.
    if (this.#shouldUpdatePriceOnChange() && this.#lastTier) {
      this.#updateProductPriceFromTier(this.#lastTier);
    }
  };

  /**
   * Sync tier selection when quantity is changed via quantity selector.
   *
   * Listens to the global Theme quantity selector update event and, when the
   * event belongs to the same product as this widget, selects the tier whose
   * threshold exactly matches the new quantity.
   *
   * @param {Event} event
   */
  #onQuantitySelectorUpdate = (event) => {
    if (!this.#shouldSyncWithQuantitySelector()) return;

    const target = event.target;

    if (!(target instanceof HTMLElement)) return;

    /** @type {any} */
    const e = event;
    const detail = e?.detail;
    const rawQuantity = detail?.quantity;

    const quantity = Number.parseInt(String(rawQuantity), 10);
    if (!Number.isFinite(quantity) || quantity <= 0) return;

    // Ensure this event belongs to the same product as this widget
    const productForm = target.closest('product-form-component');
    const eventProductId =
      (productForm instanceof HTMLElement && productForm.dataset.productId) || null;
    const widgetProductId = this.dataset.dklProductId || null;

    if (!eventProductId || !widgetProductId || eventProductId.toString() !== widgetProductId.toString()) {
      return;
    }

    this.#applyClosestTierForQuantity(quantity);
  };

  /**
   * Capture the initially-selected tier on mount so the first change
   * still has a meaningful `previousTier` value.
   */
  #captureInitialTier() {
    const checked = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input.dkl-volume-picker__radio:checked')
    );
    if (!checked) return;

    const quantity = Number.parseInt(checked.value, 10);
    if (!Number.isFinite(quantity) || quantity <= 0) return;

    const rawDiscountAmount = Number.parseFloat(checked.dataset.dklDiscountAmount || '0');
    const discountType = checked.dataset.dklDiscountType || '';
    const normalizedDiscountAmount =
      discountType === 'fixedAmount' ? rawDiscountAmount / 100 : rawDiscountAmount;

    this.#lastTier = {
      index: Number.parseInt(checked.dataset.dklTierIndex || '0', 10),
      quantity,
      discountAmount: normalizedDiscountAmount,
      discountType,
      unitPriceCents: Number.parseInt(checked.dataset.dklUnitPriceCents || '0', 10),
    };
  }

  /**
   * Recomputes all tier unit prices using the provided variant price.
   *
   * @param {number} variantPriceCents
   */
  #recomputeTierPrices(variantPriceCents) {
    const inputs = /** @type {NodeListOf<HTMLInputElement>} */ (
      this.querySelectorAll('input.dkl-volume-picker__radio')
    );

    inputs.forEach((input) => {
      const discountType = input.dataset.dklDiscountType || '';
      const rawDiscountAmount = Number.parseFloat(input.dataset.dklDiscountAmount || '0');

      let unitPriceCents = Number(variantPriceCents) || 0;

      if (discountType === 'percentage') {
        const percent = rawDiscountAmount;
        const multiplier = 1 - percent / 100;
        unitPriceCents = Math.round(unitPriceCents * multiplier);
      } else if (discountType === 'fixedAmount') {
        unitPriceCents = Math.max(0, unitPriceCents - rawDiscountAmount);
      }

      input.dataset.dklUnitPriceCents = String(unitPriceCents);

      const option = input.closest('.dkl-volume-picker__option');
      const priceElement = option?.querySelector('.dkl-volume-picker__price');
      const unitSpan = priceElement?.querySelector('.dkl-volume-picker__price-unit') || null;

      if (priceElement) {
        const formatted = this.formatMoney(unitPriceCents);
        priceElement.textContent = formatted;
        if (unitSpan) {
          priceElement.appendChild(unitSpan);
        }

        // Trigger a subtle price update animation
        priceElement.classList.remove('dkl-volume-picker__price--updated');
        // Force reflow so the animation can restart
        const _ = /** @type {HTMLElement} */ (priceElement).offsetWidth;
        priceElement.classList.add('dkl-volume-picker__price--updated');
      }
    });

    // Refresh cached previousTier/currentTier base after price changes
    this.#captureInitialTier();
  }

  /**
   * Updates the primary product price component to reflect the selected tier.
   *
   * Uses the tier's unit price in cents and formats it with the widget's
   * `formatMoney` helper. Only updates the `.price` span, leaving the
   * compare-at price untouched.
   *
   * @param {{ unitPriceCents: number }} tier
   */
  #updateProductPriceFromTier(tier) {
    const productId = this.dataset.dklProductId;
    if (!productId) return;

    const scopeRoot =
      this.closest('.shopify-section, .product-details') || document;

    const productPrice = /** @type {HTMLElement | null} */ (
      scopeRoot.querySelector(`product-price[data-product-id="${productId}"]`)
    );
    if (!productPrice) return;

    const priceContainer = productPrice.querySelector('[ref="priceContainer"]');
    const priceSpan = priceContainer?.querySelector('.price');
    if (!(priceSpan instanceof HTMLElement)) return;

    // Cache the original (base) price once so we can restore it later
    if (!productPrice.dataset.dklBasePrice) {
      productPrice.dataset.dklBasePrice = priceSpan.textContent?.trim() || '';
    }

    const formatted = this.formatMoney(tier.unitPriceCents);
    priceSpan.textContent = formatted;

    // For non-sale variants mimic the theme's sale markup by showing the
    // original price as a compare-at price, but only when the theme
    // hasn't already rendered its own compare-at price.
    if (!this.#currentVariantOnSale) {
      const basePriceText = productPrice.dataset.dklBasePrice || '';
      if (!basePriceText) return;

      // If the theme has already rendered a compare-at price (for real sale pricing), don't add another synthetic one.
      const existingThemeCompare = priceContainer?.querySelector(
        '.compare-at-price:not([data-dkl-volume-compare-at])'
      );
      if (existingThemeCompare) {
        return;
      }

      /** @type {HTMLElement | null} */
      let compareSpan = priceContainer
        ? /** @type {HTMLElement | null} */ (
            priceContainer.querySelector('.compare-at-price[data-dkl-volume-compare-at="true"]')
          )
        : null;

      if (!compareSpan) {
        const group = document.createElement('span');
        group.setAttribute('role', 'group');

        const sr = document.createElement('span');
        sr.className = 'visually-hidden';
        sr.textContent = 'Regular price\u00A0';

        compareSpan = document.createElement('span');
        compareSpan.className = 'compare-at-price';
        compareSpan.setAttribute('data-dkl-volume-compare-at', 'true');
        compareSpan.textContent = basePriceText;

        group.appendChild(sr);
        group.appendChild(compareSpan);
        priceContainer?.appendChild(group);
      } else {
        compareSpan.textContent = basePriceText;
        compareSpan.removeAttribute('hidden');
      }
    }
  }

  /**
   * Restores the primary product price to its original (non-discounted) value,
   * if it was previously cached.
   */
  #resetProductPrice() {
    const productId = this.dataset.dklProductId;
    if (!productId) return;

    const scopeRoot =
      this.closest('.shopify-section, .product-details') || document;

    const productPrice = /** @type {HTMLElement | null} */ (
      scopeRoot.querySelector(`product-price[data-product-id="${productId}"]`)
    );
    if (!productPrice) return;

    const basePrice = productPrice.dataset.dklBasePrice;
    if (!basePrice) return;

    const priceContainer = productPrice.querySelector('[ref="priceContainer"]');
    const priceSpan = priceContainer?.querySelector('.price');
    if (!(priceSpan instanceof HTMLElement)) return;

    priceSpan.textContent = basePrice;

    // Remove any synthetic compare-at price we added for non-sale variants
    const syntheticCompare = priceContainer?.querySelector(
      '.compare-at-price[data-dkl-volume-compare-at="true"]'
    );
    if (syntheticCompare instanceof HTMLElement) {
      const group = syntheticCompare.closest('span[role="group"]');
      if (group && group.parentElement) {
        group.parentElement.removeChild(group);
      } else {
        syntheticCompare.remove();
      }
    }
  }

  /**
   * Disables all tier radios and clears any active selection. Used when the
   * active variant is on sale and this widget is configured to exclude sale prices.
   */
  #disableForSaleVariant() {
    const radios = /** @type {NodeListOf<HTMLInputElement>} */ (
      this.querySelectorAll('input.dkl-volume-picker__radio')
    );

    radios.forEach((radio) => {
      radio.disabled = true;
      radio.checked = false;
    });

    this.#lastTier = null;
  }

  /**
   * Re-enables all tier radios for non-sale variants so the widget can be used normally.
   */
  #enableForNonSaleVariant() {
    const radios = /** @type {NodeListOf<HTMLInputElement>} */ (
      this.querySelectorAll('input.dkl-volume-picker__radio')
    );

    radios.forEach((radio) => {
      radio.disabled = false;
    });

    // Ensure unit labels are visible again after leaving a sale-only state
    const priceElements = /** @type {NodeListOf<HTMLElement>} */ (
      this.querySelectorAll('.dkl-volume-picker__price')
    );

    priceElements.forEach((priceEl) => {
      const unitSpan = priceEl.querySelector('.dkl-volume-picker__price-unit');
      if (unitSpan instanceof HTMLElement) {
        unitSpan.removeAttribute('hidden');
      }
    });
  }

  /**
   * Updates all tier price labels to indicate that the discount is not
   * available for the currently selected variant. Used when the variant
   * is on sale and sale-price exclusions are enabled.
   */
  #setPriceUnavailableForSaleVariant() {
    const priceElements = /** @type {NodeListOf<HTMLElement>} */ (
      this.querySelectorAll('.dkl-volume-picker__price')
    );

    priceElements.forEach((priceEl) => {
      // Hide the unit label for this state
      const unitSpan = priceEl.querySelector('.dkl-volume-picker__price-unit');
      if (unitSpan instanceof HTMLElement) {
        unitSpan.setAttribute('hidden', 'true');
      }

      // Replace the main text content while preserving the span structure
      const firstChild = priceEl.firstChild;
      if (firstChild && firstChild.nodeType === Node.TEXT_NODE) {
        firstChild.textContent = this.priceUnavailableLabel;
      } else {
        priceEl.textContent = this.priceUnavailableLabel;
      }
    });
  }

  /**
   * Reads the current quantity from the nearest product form's
   * quantity selector or quantity input.
   *
   * @returns {number | null}
   */
  #getCurrentQuantityFromForm() {
    const productId = this.dataset.dklProductId || null;
    const scopeRoot =
      this.closest('.shopify-section, .product-details') || this.parentElement || document;

    /** @type {HTMLElement | null} */
    let productForm = null;

    if (productId) {
      productForm =
        scopeRoot.querySelector(`product-form-component[data-product-id="${productId}"]`) ||
        document.querySelector(`product-form-component[data-product-id="${productId}"]`);
    } else {
      productForm =
        scopeRoot.querySelector('product-form-component') ||
        document.querySelector('product-form-component');
    }

    if (!(productForm instanceof HTMLElement)) return null;

    /** @type {any} */
    const quantitySelector = productForm.querySelector('quantity-selector-component');
    if (quantitySelector?.getValue) {
      const value = Number.parseInt(String(quantitySelector.getValue()), 10);
      return Number.isFinite(value) && value > 0 ? value : null;
    }

    const quantityInput = /** @type {HTMLInputElement | null} */ (
      productForm.querySelector('input[name="quantity"]')
    );
    if (!quantityInput) return null;

    const value = Number.parseInt(quantityInput.value, 10);
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  /**
   * Applies the closest tier for a given quantity using the same logic
   * as quantity-selector sync, without modifying the quantity itself.
   *
   * @param {number} quantity
   */
  #applyClosestTierForQuantity(quantity) {
    // Find the closest tier that is <= the new quantity.
    // Example tiers: 2, 5, 10
    // - qty 7  => select 5
    // - qty 1  => select none
    const radios = /** @type {NodeListOf<HTMLInputElement>} */ (
      this.querySelectorAll('input.dkl-volume-picker__radio')
    );

    /** @type {HTMLInputElement | null} */
    let closestRadio = null;
    let closestQty = -Infinity;

    for (const radio of radios) {
      const radioQty = Number.parseInt(radio.value, 10);
      if (!Number.isFinite(radioQty)) continue;
      if (radioQty <= quantity && radioQty > closestQty) {
        closestQty = radioQty;
        closestRadio = radio;
      }
    }

    // If quantity is below the smallest tier, don't auto-select anything
    if (!closestRadio || closestQty <= 0) {
      // Quantity is below the smallest tier – clear any existing selection.
      const checked = /** @type {HTMLInputElement | null} */ (
        this.querySelector('input.dkl-volume-picker__radio:checked')
      );
      if (checked) {
        checked.checked = false;
      }
      this.#lastTier = null;
      if (this.#shouldUpdatePriceOnChange()) {
        this.#resetProductPrice();
      }
      return;
    }

    if (!closestRadio.checked) {
      closestRadio.checked = true;
      // Fire a change event so existing #onChange logic runs (updates quantity, events, etc)
      const changeEvent = new Event('change', { bubbles: true });
      /** @type {any} */ (changeEvent).__fromQuantitySelectorSync = true;
      closestRadio.dispatchEvent(changeEvent);
    }
  }

}

// Mini-buttons variant: reuse all behavior from the radio-groups variant but register a distinct
// custom element constructor incase it ever needs isolating in the future.
class DklVolumePickerMiniButtons extends DklVolumePickerRadioGroups {}

if (typeof customElements !== 'undefined') {
  if (!customElements.get('dkl-volume-picker-radio-groups')) {
    customElements.define('dkl-volume-picker-radio-groups', DklVolumePickerRadioGroups);
  }

  if (!customElements.get('dkl-volume-picker-mini-buttons')) {
    customElements.define('dkl-volume-picker-mini-buttons', DklVolumePickerMiniButtons);
  }
}
